import { getConfig } from '@superbet-group/web.lib.config';
import { createCTRestClient } from '@superbet-group/web.lib.rest';
import {
    delay,
    IResponse,
    isNumber,
} from '@superbet-group/web.lib.utilities';
import { getClientSourceParam } from '@superbet-group/web.lib.vue-utilities';

import { defaultHttpTimeoutMs } from '@core/constants';
import { transformLimits } from '@core/mappers/user/transformLimits';
import {
    LimitDto,
    TimeOutDto,
} from '@core/mappers/user/dtos/LimitsDto';
import { getCsrfToken } from '@core/services/authentication/helpers';
import {
    CloseAccountMfaPayload,
    ReasonPayload,
} from '@core/services/user/_private/Types';
import {
    LimitType,
    TimeType,
    LimitStatus,
    Limits,
} from '@models/user/Limit';

const ms = require('ms');

type PossibleError = { error?: { notice: string }};
type EmptySuccess = { error: false; notice?: string };
type LimitsResponse = IResponse<EmptySuccess & PossibleError>;

const endpoints = getConfig().services.playerAccountManagement;
const restClient = createCTRestClient(
    getConfig().auth.host,
    defaultHttpTimeoutMs,
    getClientSourceParam(),
    true,
    getCsrfToken,
);

const limitTypeMap: Record<string, LimitType> = {
    deposit: LimitType.Deposit,
    stake: LimitType.TotalWager,
    loss: LimitType.Loss,
    session: LimitType.Session,
};

const limitStatusMap: Record<string, LimitStatus> = {
    active: LimitStatus.Active,
    pending_update: LimitStatus.Inactive,
    pending_cancel: LimitStatus.Inactive,
};

const limitTimeMap: Record<string, TimeType> = {
    daily: TimeType.Day,
    weekly: TimeType.Week,
    monthly: TimeType.Month,
};

export async function getLimits(): Promise<Limits> {
    let limits: LimitDto[] = await getPlayerLimits();
    limits = limits.filter((limit) => !(limit.LimitStatus === LimitStatus.Active && limit.AmountValue === 0));
    return transformLimits(limits);
}

export async function setLimits(data: {
    limitType: LimitType;
    amount: number;
    time: TimeType;
}[]) {
    // temporary backend fix. Requests can't come too close together
    let i = data.length - 1;
    let res: (void | LimitsResponse)[];
    for (const limit of data) {
        i -= 1;
        if (i > 0) {
            // eslint-disable-next-line no-await-in-loop
            res = await Promise.all([delay(ms('1.5s')), setPlayerLimit(limit)]);
        } else {
            // eslint-disable-next-line no-await-in-loop
            res = [await setPlayerLimit(limit)];
        }

        const errorResponse = res?.find((item) => item?.error);
        if (errorResponse) {
            throw new Error(errorResponse?.notice);
        }
    }
}

export function cancelLimit(limitId: number) {
    return restClient.post(
        endpoints.cancelPlayerLimit,
        { limitId },
    );
}

export async function timeOut(toDate: Date) {
    const res = await restClient.post<TimeOutDto, LimitsResponse>(
        endpoints.timeOutPlayer,
        { toDate },
    );

    if (res.error) {
        throw new Error(res.notice);
    }

    return res.data;
}

export async function selfExclude(toDate: Date) {
    const res = await restClient.post<Record<string, any>, LimitsResponse>(endpoints.selfExcludePlayer, { to_date: toDate });

    if (res.error) {
        throw new Error(res.notice);
    }

    return res;
}

export async function closeAccount(reasonPayload?: ReasonPayload) {
    const res = await restClient.post<Partial<ReasonPayload>, LimitsResponse>(
        endpoints.closeAccount,
        {
            ...(reasonPayload ?? {}),
        },
    );

    if (res.error) {
        throw new Error(res.notice);
    }

    return res;
}

export async function closeAccountMfa(payload: CloseAccountMfaPayload) {
    const res = await restClient.post<Partial<ReasonPayload>, LimitsResponse>(
        endpoints.closeAccountMfa!,
        {
            ...(payload),
        },
    );

    if (res.error) {
        throw new Error(res.notice);
    }

    return res;
}

function setPlayerLimit({
    limitType,
    amount,
    time,
}: {
    limitType: LimitType;
    amount: number;
    time: TimeType;
}): Promise<LimitsResponse> {
    return restClient.post<Record<string, any>, LimitsResponse>(
        endpoints.setPlayerLimits,
        {
            limit: {
                LimitType: `${limitType}`,
                AmountValue: `${amount}`,
                Time: `${time}`,
            },
        },
    );
}

async function getPlayerLimits() {
    const res = await restClient.get<IResponse<any> & PossibleError>(endpoints.getPlayerLimits);
    if (res.error) {
        throw new Error(res.error.notice);
    }

    if (res.data.Limits) {
        return res.data.Limits.map((limit: any) => ({
            // TODO: Temporary hack for V2 pending_cancel
            AmountValue: limit.status === 'pending_cancel' ? 0 : limit.amount,
            Id: limit.id,
            LimitStatus: limitStatusMap[limit.status],
            LimitType: limitTypeMap[limit.type],
            Time: limitTimeMap[limit.interval],
            LimitRemaining: isNumber(limit.limit_remaining) ? limit.limit_remaining : limit.amount,
        }));
    }

    return res.data;
}
